<script setup lang="ts">
import { computed, ref } from "vue";
import { useCurveModelStore } from "@/modules/booking_experts/store";
import useDialog from "@/ui/composables/useDialog";
import PriceModelCurveBaseDataDO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurveBaseDataDO;
import Switch from "@/ui/components/switch/switch.vue";
import { createBezierCurve } from "@/modules/booking_experts/utils";

const dialog = useDialog();
const store = useCurveModelStore();
const showMenu = ref(false);

// Visibility controls
const curveType = computed(() => store.currentCurve.type);
const hasPYData = computed(() => store.referenceData.y1.length > 0);
const showAlignToTarget = computed(() =>
	["custom", "occupancy"].includes(curveType.value)
);

const showSnapToBase = computed(
	() => curveType.value === "custom" && hasPYData.value
);

async function adjustCurveToBaseData(shouldScale: boolean = false) {
	const choice = await dialog.confirm({
		title: `${shouldScale ? "Align" : "Snap"} curve?`,
		message: shouldScale
			? "Scale curve to target occupancy percentage"
			: "Copy previous year's curve pattern",
	});

	if (!choice) return;

	switch (curveType.value) {
		case "occupancy":
			createFlatLine();
			break;
		case "year_over_year":
			if (shouldScale) {
				store.currentCurve.base_dataset = createBezierCurve(
					store.currentCurve.days,
					store.currentCurve.yoy_options.scaleTarget
				);
			}
			break;
		default:
			adjustRegularCurve(shouldScale);
	}

	showMenu.value = false;
}

function createFlatLine() {
	store.currentCurve.base_dataset = [
		{ x: store.currentCurve.days, y: store.targetPercentage },
		{ x: 0, y: store.targetPercentage },
	];
}

function adjustRegularCurve(shouldScale: boolean) {
	const baseData = store.referenceData;
	if (!baseData.y1) return;

	const sortedData = [...baseData.y1].sort((a, b) => b.x - a.x);
	const scaleFactor = shouldScale
		? store.currentCurve.yoy_options.scaleTarget / (sortedData.at(-1)?.y || 1)
		: 1;

	let previousY = -1;
	// Should do the same as BE code in TargetLineService
	const newData = sortedData
		.filter((p) => p.y <= sortedData.at(-1).y)
		.reduce<PriceModelCurveBaseDataDO[]>((acc, point) => {
			const y = Math.min(
				100,
				Math.max(0, shouldScale ? point.y * scaleFactor : point.y)
			);
			if (y <= previousY) return acc;

			previousY = y;
			acc.push({ x: point.x, y });
			return acc;
		}, []);

	// Ensure start and end points exist
	if (newData[0]?.x !== store.currentCurve.days) {
		newData.unshift({ x: store.currentCurve.days, y: 0 });
	}

	if (newData.at(-1)?.x !== 0) {
		newData.push({ x: 0, y: newData.at(-1)?.y ?? 0 });
	}

	store.currentCurve.base_dataset = newData.reverse();
}

const explanationText = computed(() => {
	if (curveType.value === "occupancy") {
		return "Set target occupancy percentage";
	}

	if (
		curveType.value === "year_over_year" &&
		store.currentCurve.yoy_options.mode === "offset"
	) {
		return "Adjust curve based on previous year's data, adjusted with an offset.";
	}

	return "Adjust curve based on previous year's data";
});

const yoyIsOffsetMode = computed({
	get() {
		return store.currentCurve.yoy_options.mode === "offset";
	},
	set(v: boolean) {
		store.currentCurve.yoy_options.mode = v ? "offset" : "target";
	},
});

const yoyValue = computed({
	get() {
		if (
			curveType.value === "year_over_year" &&
			store.currentCurve.yoy_options.mode === "offset"
		) {
			return store.currentCurve.yoy_options.scaleOffset;
		}

		return store.currentCurve.yoy_options.scaleTarget;
	},
	set(v) {
		if (
			curveType.value === "year_over_year" &&
			store.currentCurve.yoy_options.mode === "offset"
		) {
			store.currentCurve.yoy_options.scaleOffset = v;

			return;
		}

		store.currentCurve.yoy_options.scaleTarget = v;
	},
});
</script>

<template>
	<v-menu v-model="showMenu" location="bottom" :close-on-content-click="false">
		<template #activator="{ props }">
			<v-btn
				v-bind="props"
				variant="flat"
				color="secondary"
				size="small"
				prepend-icon="fa-solid fa-chart-line"
			>
				Align Curve
			</v-btn>
		</template>

		<v-card max-width="500" class="pa-4">
			<p class="text-medium-emphasis text-sm-caption">
				{{ explanationText }}
			</p>
			<div v-if="curveType === 'year_over_year'" class="mt-2">
				<Switch
					class="mt-2"
					label="Scale the year-over-year target curve"
					v-model="store.currentCurve.yoy_options.shouldScale"
				/>
			</div>
			<div
				class="d-flex align-center mt-4"
				v-if="
					curveType !== 'year_over_year' ||
					store.currentCurve.yoy_options.shouldScale
				"
			>
				<v-select
					v-if="curveType === 'year_over_year'"
					:items="[
						{ label: 'Target', value: 'target' },
						{ label: 'Offset', value: 'offset' },
					]"
					v-model="store.currentCurve.yoy_options.mode"
					item-title="label"
					item-value="value"
					density="compact"
					hide-details
					hide-spin-buttons
					class="mr-2"
				/>
				<v-text-field
					v-model.number="yoyValue"
					:label="
						store.currentCurve.yoy_options.mode === 'target'
							? 'Target'
							: 'Offset'
					"
					append-inner-icon="fa-regular fa-percentage"
					type="number"
					density="compact"
					hide-details
					hide-spin-buttons
					min="0"
					max="100"
					step="0.5"
				/>
			</div>

			<div class="d-flex flex-column ga-2">
				<v-btn
					v-if="showAlignToTarget"
					variant="flat"
					color="secondary"
					class="mt-2"
					block
					:disabled="
						!hasPYData && !['occupancy', 'year_over_year'].includes(curveType)
					"
					@click="adjustCurveToBaseData(true)"
				>
					{{ curveType === "occupancy" ? "Set target" : "Align to target" }}
				</v-btn>

				<v-btn
					v-if="showSnapToBase"
					variant="flat"
					color="secondary"
					block
					:disabled="!hasPYData"
					@click="adjustCurveToBaseData(false)"
				>
					Snap to base
				</v-btn>
			</div>
		</v-card>
	</v-menu>
</template>

<style scoped lang="scss">
:deep(.v-field__append-inner) {
	font-size: 0.7em;
}
</style>
